import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';

interface BlogCategory { uid: string; name: string; slug: string; }
interface BlogTag { uid: string; name: string; slug: string; }
interface BlogPost {
  uid: string;
  slug: string;
  title: string;
  excerpt?: string;
  status: string;
  published_at?: string;
  author_name?: string;
  category?: BlogCategory;
  tags: BlogTag[];
  created_at: string;
  updated_at: string;
}
interface BlogListResponse {
  items: BlogPost[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
}

const ADMIN_NAV_LINK = ({ isActive }: { isActive: boolean }) =>
  `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`;

const AdminBlog: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<BlogListResponse | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployMsg, setDeployMsg] = useState<string | null>(null);

  const [newCatName, setNewCatName] = useState('');
  const [newCatSlug, setNewCatSlug] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagSlug, setNewTagSlug] = useState('');
  const [catLoading, setCatLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);

  useEffect(() => { document.title = 'Admin | Blog'; }, []);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    const url = `${API_ENDPOINTS.admin.blog}?page=${page}&limit=20${statusFilter ? `&status=${statusFilter}` : ''}`;
    api.get<BlogListResponse>(url)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  useEffect(() => {
    api.get<BlogCategory[]>(API_ENDPOINTS.blog.categories).then(setCategories).catch(() => {});
    api.get<BlogTag[]>(API_ENDPOINTS.blog.tags).then(setTags).catch(() => {});
  }, []);

  const handleDelete = async (uid: string, title: string) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await api.delete(API_ENDPOINTS.admin.blogPost(uid));
    fetchPosts();
  };

  const handleDeploy = async () => {
    setDeploying(true);
    setDeployMsg(null);
    try {
      const res = await api.post<{ status: string; pipeline_id?: number }>(
        API_ENDPOINTS.admin.blogDeploy, {}
      );
      setDeployMsg(`Pipeline #${res.pipeline_id ?? '—'} triggered`);
    } catch {
      setDeployMsg('Failed to trigger deploy');
    } finally {
      setDeploying(false);
    }
  };

  const slugify = (v: string) =>
    v.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-');

  const handleCreateCategory = async () => {
    if (!newCatName || !newCatSlug) return;
    setCatLoading(true);
    try {
      const cat = await api.post<BlogCategory>(API_ENDPOINTS.admin.blogCreateCategory, {
        name: newCatName, slug: newCatSlug,
      });
      setCategories(prev => [...prev, cat]);
      setNewCatName(''); setNewCatSlug('');
    } catch {}
    setCatLoading(false);
  };

  const handleDeleteCategory = async (uid: string) => {
    await api.delete(API_ENDPOINTS.admin.blogCategory(uid));
    setCategories(prev => prev.filter(c => c.uid !== uid));
  };

  const handleCreateTag = async () => {
    if (!newTagName || !newTagSlug) return;
    setTagLoading(true);
    try {
      const tag = await api.post<BlogTag>(API_ENDPOINTS.admin.blogCreateTag, {
        name: newTagName, slug: newTagSlug,
      });
      setTags(prev => [...prev, tag]);
      setNewTagName(''); setNewTagSlug('');
    } catch {}
    setTagLoading(false);
  };

  const handleDeleteTag = async (uid: string) => {
    await api.delete(API_ENDPOINTS.admin.blogTag(uid));
    setTags(prev => prev.filter(t => t.uid !== uid));
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 1;

  return (
    <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'Blog' }]}>
      <nav className="admin-nav">
        <NavLink to="/admin" end className={ADMIN_NAV_LINK}>Overview</NavLink>
        <NavLink to="/admin/users" className={ADMIN_NAV_LINK}>Users</NavLink>
        <NavLink to="/admin/ai-usage" className={ADMIN_NAV_LINK}>AI Usage</NavLink>
        <NavLink to="/admin/ai-settings" className={ADMIN_NAV_LINK}>AI Settings</NavLink>
        <NavLink to="/admin/plans" className={ADMIN_NAV_LINK}>Plans</NavLink>
        <NavLink to="/admin/blog" className={ADMIN_NAV_LINK}>Blog</NavLink>
      </nav>

      <div className="admin-toolbar" style={{ marginBottom: 16 }}>
        <div className="admin-chip-group" role="tablist" aria-label="Filter by status">
          {(['', 'draft', 'scheduled', 'published'] as const).map(s => {
            const label = s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1);
            const active = statusFilter === s;
            return (
              <button
                key={s || 'all'}
                type="button"
                className={`admin-chip ${active ? 'admin-chip--active' : ''}`}
                onClick={() => { setStatusFilter(s); setPage(1); }}
              >
                {label}
              </button>
            );
          })}
        </div>
        <button
          className="admin-btn admin-btn--primary"
          onClick={() => navigate('/admin/blog/new')}
          style={{ marginLeft: 'auto' }}
        >
          + New post
        </button>
        <button
          className="admin-btn admin-btn--ghost"
          onClick={handleDeploy}
          disabled={deploying}
        >
          {deploying ? 'Deploying…' : 'Deploy blog'}
        </button>
        {deployMsg && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>{deployMsg}</span>
        )}
      </div>

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Title</th>
                <th style={{ textAlign: 'center' }}>Category</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Published</th>
                <th style={{ textAlign: 'center' }}>Author</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!data?.items.length && (
                <tr>
                  <td colSpan={6} className="admin-empty">No posts yet</td>
                </tr>
              )}
              {data?.items.map(post => (
                <tr key={post.uid}>
                  <td>
                    <span style={{ fontWeight: 600 }}>{post.title}</span>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{post.slug}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {post.category
                      ? <span className="admin-badge admin-badge--blue">{post.category.name}</span>
                      : <span style={{ color: '#9ca3af' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span
                      className={`admin-badge ${
                        post.status === 'published'
                          ? 'admin-badge--green'
                          : post.status === 'scheduled'
                            ? 'admin-badge--amber'
                            : 'admin-badge--gray'
                      }`}
                    >
                      {post.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    {post.published_at
                      ? (post.status === 'scheduled' ? 'Scheduled: ' : '') +
                        new Date(post.published_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: post.status === 'scheduled' ? '2-digit' : undefined,
                          minute: post.status === 'scheduled' ? '2-digit' : undefined,
                        })
                      : '—'}
                  </td>
                  <td style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>{post.author_name || '—'}</td>
                  <td>
                    <div className="admin-btn-group">
                      <button
                        className="admin-btn admin-btn--outline admin-btn--sm"
                        onClick={() => navigate(`/admin/blog/${post.uid}`)}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-btn admin-btn--danger admin-btn--sm"
                        onClick={() => handleDelete(post.uid, post.title)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="admin-pagination">
          <button
            className="admin-pagination__btn"
            onClick={() => setPage(p => p - 1)}
            disabled={page <= 1}
          >
            ←
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            className="admin-pagination__btn"
            onClick={() => setPage(p => p + 1)}
            disabled={!data?.has_next}
          >
            →
          </button>
        </div>
      )}

      <p className="admin-section-title" style={{ marginTop: 40 }}>Categories</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="admin-search"
          style={{ maxWidth: 200 }}
          placeholder="Name"
          value={newCatName}
          onChange={e => { setNewCatName(e.target.value); setNewCatSlug(slugify(e.target.value)); }}
        />
        <input
          className="admin-search"
          style={{ maxWidth: 200 }}
          placeholder="slug"
          value={newCatSlug}
          onChange={e => setNewCatSlug(e.target.value)}
        />
        <button
          className="admin-btn admin-btn--primary"
          onClick={handleCreateCategory}
          disabled={catLoading || !newCatName || !newCatSlug}
        >
          Add
        </button>
      </div>
      <div className="admin-table-wrap admin-table-wrap--scroll">
        <table className="admin-table">
          <thead><tr><th>Name</th><th>Slug</th><th></th></tr></thead>
          <tbody>
            {!categories.length && (
              <tr><td colSpan={3} className="admin-empty">No categories</td></tr>
            )}
            {categories.map(cat => (
              <tr key={cat.uid}>
                <td>{cat.name}</td>
                <td><code style={{ fontSize: 12 }}>{cat.slug}</code></td>
                <td>
                  <button
                    className="admin-btn admin-btn--danger admin-btn--sm"
                    onClick={() => handleDeleteCategory(cat.uid)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="admin-section-title" style={{ marginTop: 32 }}>Tags</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="admin-search"
          style={{ maxWidth: 200 }}
          placeholder="Name"
          value={newTagName}
          onChange={e => { setNewTagName(e.target.value); setNewTagSlug(slugify(e.target.value)); }}
        />
        <input
          className="admin-search"
          style={{ maxWidth: 200 }}
          placeholder="slug"
          value={newTagSlug}
          onChange={e => setNewTagSlug(e.target.value)}
        />
        <button
          className="admin-btn admin-btn--primary"
          onClick={handleCreateTag}
          disabled={tagLoading || !newTagName || !newTagSlug}
        >
          Add
        </button>
      </div>
      <div className="admin-table-wrap admin-table-wrap--scroll">
        <table className="admin-table">
          <thead><tr><th>Name</th><th>Slug</th><th></th></tr></thead>
          <tbody>
            {!tags.length && (
              <tr><td colSpan={3} className="admin-empty">No tags</td></tr>
            )}
            {tags.map(tag => (
              <tr key={tag.uid}>
                <td>{tag.name}</td>
                <td><code style={{ fontSize: 12 }}>{tag.slug}</code></td>
                <td>
                  <button
                    className="admin-btn admin-btn--danger admin-btn--sm"
                    onClick={() => handleDeleteTag(tag.uid)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
};

export default AdminBlog;
